import { useDispatch, useSelector } from 'react-redux';
import { styled, SwipeableDrawer, useMediaQuery } from '@mui/material';

import { navbarCloseFolded, navbarOpenFolded, navbarCloseMobile } from 'app/store/fuse/navbarSlice';
import NavbarStyle2Content from './NavbarStyle2Content';

interface NavbarProps {
	position: 'left' | 'right';
	folded: boolean;
	foldedAndOpened: boolean;
	foldedAndClosed: boolean;
}

const navbarWidth = 280;

const Root = styled('div')<{ folded: boolean }>(({ theme, folded }) => ({
	display: 'flex',
	flexDirection: 'column',
	zIndex: 4,
	[theme.breakpoints.up('lg')]: {
		width: navbarWidth,
		minWidth: navbarWidth
	},

	...(folded && {
		[theme.breakpoints.up('lg')]: {
			width: 64,
			minWidth: 64
		}
	})
}));

const StyledNavbar = styled('div')<NavbarProps>(({ theme, position, folded, foldedAndOpened, foldedAndClosed }) => ({
	minWidth: navbarWidth,
	width: navbarWidth,
	maxWidth: navbarWidth,
	maxHeight: '100%',
	transition: theme.transitions.create(['width', 'min-width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.shorter
	}),
	...(position === 'left' && {
		left: 0
	}),
	...(position === 'right' && {
		right: 0
	}),
	...(folded && {
		position: 'absolute',
		width: 64,
		minWidth: 64,
		top: 0,
		bottom: 0
	}),
	...(foldedAndOpened && {
		width: navbarWidth,
		minWidth: navbarWidth
	}),
	...(foldedAndClosed && {
		'& .NavbarStyle2-content': {
			'& .logo-icon': {
				width: 32,
				height: 32
			},
			'& .logo-text': {
				opacity: 0
			},
			'& .react-badge': {
				opacity: 0
			},
			'& .fuse-list-item-text, & .arrow-icon, & .item-badge': {
				opacity: 0
			},
			'& .fuse-list-subheader .fuse-list-subheader-text': {
				opacity: 0
			},
			'& .fuse-list-subheader:before': {
				content: '""',
				display: 'block',
				position: 'absolute',
				minWidth: 16,
				borderTop: '2px solid',
				opacity: 0.2
			},
			'& .collapse-children': {
				display: 'none'
			},
			'& .user': {
				'& .username, & .email': {
					opacity: 0
				},
				'& .avatar': {
					width: 40,
					height: 40,
					bottom: 104,
					padding: 0
				}
			},
			'& .fuse-list-item.active': {
				width: 44
			}
		}
	})
}));

const StyledNavbarMobile = styled(SwipeableDrawer)(({ theme }) => ({
	'& > .MuiDrawer-paper': {
		minWidth: navbarWidth,
		width: navbarWidth,
		maxWidth: navbarWidth,
		maxHeight: '100%',
		transition: theme.transitions.create(['width', 'min-width'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.shorter
		})
	}
}));

function NavbarStyle2() {
	const dispatch = useDispatch();
	const config = useSelector(({ fuse }: any) => fuse.settings.current.layout.config);
	const navbar = useSelector(({ fuse }: any) => fuse.navbar);
	const isLgUp = useMediaQuery(theme => theme.breakpoints.up('lg'));

	// const folded = !navbar.open;
	const { folded } = config.navbar;
	const foldedAndClosed = folded && !navbar.foldedOpen;
	const foldedAndOpened = folded && navbar.foldedOpen;

	return (
		<Root folded={folded} id="fuse-navbar" className="sticky top-0 z-20 h-screen shrink-0 shadow-5">
			{isLgUp ? (
				<StyledNavbar
					className="flex-auto flex-col"
					position={config.navbar.position}
					folded={folded}
					foldedAndOpened={foldedAndOpened}
					foldedAndClosed={foldedAndClosed}
					onMouseEnter={() => {
						if (foldedAndClosed) dispatch(navbarOpenFolded());
					}}
					onMouseLeave={() => {
						if (foldedAndOpened) dispatch(navbarCloseFolded());
					}}
				>
					<NavbarStyle2Content className="NavbarStyle2-content" />
				</StyledNavbar>
			) : (
				<StyledNavbarMobile
					classes={{
						paper: 'flex-col flex-auto h-full'
					}}
					anchor={config.navbar.position}
					variant="temporary"
					open={navbar.mobileOpen}
					onClose={() => dispatch(navbarCloseMobile())}
					onOpen={() => {}}
					disableSwipeToOpen
					ModalProps={{
						keepMounted: true // Better open performance on mobile.
					}}
				>
					<NavbarStyle2Content className="NavbarStyle2-content" />
				</StyledNavbarMobile>
			)}
		</Root>
	);
}

export default NavbarStyle2;
